function initAboutSection() {
  if (typeof gsap === "undefined") return;

  const section = document.getElementById("about-section");
  if (!section) return;

  const glass = document.getElementById("glass");
  const clear = document.getElementById("about-clear");
  const blur = document.getElementById("about-blur");

  let hasPlayed = false;
  let resizeRAF;

  /* ------------------ INITIALIZE IMMEDIATELY IF ALREADY LOADED ------------------ */
  function initImmediately() {
    // Force scroll to top if needed
    if (window.scrollY > 0) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
    // Small delay to let scroll happen
    setTimeout(() => {
      init();
    }, 150);
  }

  /* ------------------ MASK SYNC ------------------ */
  function updateMask() {
    const rect = glass.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();

    const posX = rect.left - sectionRect.left;
    const posY = rect.top - sectionRect.top;
    const scaledW = rect.width;
    const scaledH = rect.height;

    clear.style.maskPosition = `${posX}px ${posY}px`;
    clear.style.webkitMaskPosition = `${posX}px ${posY}px`;
    clear.style.maskSize = `${scaledW}px ${scaledH}px`;
    clear.style.webkitMaskSize = `${scaledW}px ${scaledH}px`;
  }

  /* ------------------ SCROLL LOCK/UNLOCK ------------------ */
  function preventScroll(e) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  function lockScroll() {
    window.addEventListener("wheel", preventScroll, { passive: false });
    window.addEventListener("touchmove", preventScroll, { passive: false });
    window.addEventListener("keydown", preventScroll, { passive: false });
  }

  function unlockScroll() {
    window.removeEventListener("wheel", preventScroll);
    window.removeEventListener("touchmove", preventScroll);
    window.removeEventListener("keydown", preventScroll);
  }

  /* ------------------ SMOOTH SCROLL ------------------ */
  function smoothScrollTo(y, duration = 1.2, ease = "power2.inOut") {
    const targetY = Math.max(0, y);
    const root = document.documentElement;
    const prevBehavior = root.style.scrollBehavior || "";

    root.style.scrollBehavior = "auto";

    return gsap.to(window, {
      scrollTo: { y: targetY, autoKill: false },
      duration: duration,
      ease,
      overwrite: "auto",
      immediateRender: true,
      onComplete: () => {
        root.style.scrollBehavior = prevBehavior;
      },
    });
  }

  /* ------------------ TIMELINE ------------------ */
  function createTimeline() {
    const tl = gsap.timeline({
      defaults: { ease: "power2.inOut" },
      onUpdate: updateMask,
    });

    tl.to(glass, {
      duration: 1.5,
      y: "-42vh",
      ease: "power2.out",
    });

    tl.to({}, { duration: 0.2 });

    tl.to(glass, {
      duration: 2.2,
      scale: 19,
      ease: "power3.inOut",
    });

    tl.call(() => {
      clear.style.maskImage = "none";
      clear.style.webkitMaskImage = "none";
      if (blur) blur.style.display = "none";
      if (glass) glass.style.display = "none";
    });

    return tl;
  }

  let savedMode;
  /* ------------------ ANIMATION HANDLER ------------------ */
  async function runAnimationOnce() {
    if (hasPlayed) return;

    // Lock scroll to prevent overscrolling
    lockScroll();
    savedMode = HeaderControl.getMode();
    HeaderControl.setMode("inverse");

    // Scroll to section top - make section fully visible
    const sectionTop = section.offsetTop;

    await smoothScrollTo(sectionTop, 1.0);

    // Ensure elements are visible
    gsap.set(section, { height: "100dvh", minHeight: "100dvh" });
    if (blur) blur.style.display = "block";
    if (glass) glass.style.display = "block";

    // Wait a frame for layout
    await new Promise(requestAnimationFrame);

    // Create and play timeline
    const tl = createTimeline();
    tl.eventCallback("onUpdate", updateMask);
    tl.eventCallback("onComplete", () => {
      clear.style.maskImage = "none";
      clear.style.webkitMaskImage = "none";
      if (blur) blur.style.display = "none";
      if (glass) glass.style.display = "none";
      // Unlock scroll when animation completes
      unlockScroll();
      HeaderControl.setMode(savedMode);
    });

    tl.play();
    hasPlayed = true;
  }

  /* ------------------ INTERSECTION OBSERVER ------------------ */
  let observer;

  function createObserver() {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // When 25% of section is visible, trigger animation
          if (entry.isIntersecting && !hasPlayed) {
            runAnimationOnce();
          }
        });
      },
      {
        threshold: 0.4, // Trigger when 40% of section is shown
        rootMargin: "0px",
      }
    );
    observer.observe(section);
  }

  /* ------------------ INITIALIZE ------------------ */
  function init() {
    // Disconnect existing observer if any
    if (observer) {
      observer.disconnect();
    }

    // Reset state
    hasPlayed = false;

    // Reset visual elements
    gsap.set(section, { height: "100dvh", minHeight: "100dvh" });
    if (blur) blur.style.display = "block";
    if (glass) glass.style.display = "block";

    // Create and start observer
    createObserver();
    requestAnimationFrame(updateMask);

    // Check if section is already in viewport
    const rect = section.getBoundingClientRect();
    const isInViewport =
      rect.top < window.innerHeight * 0.75 && rect.bottom > 0;
    if (isInViewport && !hasPlayed) {
      runAnimationOnce();
    }
  }

  /* ------------------ LOAD ------------------ */
  window.addEventListener("load", () => {
    // Force scroll to top if needed
    if (window.scrollY > 0) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
    // Initialize immediately
    setTimeout(() => {
      init();
    }, 100);
  });

  /* ------------------ RESIZE ------------------ */
  window.addEventListener(
    "resize",
    () => {
      cancelAnimationFrame(resizeRAF);
      resizeRAF = requestAnimationFrame(() => {
        updateMask();
      });
    },
    { passive: true }
  );

  /* ------------------ CLEANUP ------------------ */
  function cleanup() {
    if (resizeRAF) {
      cancelAnimationFrame(resizeRAF);
      resizeRAF = null;
    }
    observer?.disconnect();
    unlockScroll();
  }

  window.addEventListener("beforeunload", cleanup);

  /* ------------------ BFCACHE & VISIBILITY ------------------ */
  window.addEventListener("pageshow", (event) => {
    if (
      event.persisted ||
      performance.getEntriesByType("navigation")[0]?.type === "back_forward"
    ) {
      cleanup();
      unlockScroll();
      hasPlayed = false;

      // Reset elements to initial state
      if (glass) {
        glass.style.display = "block";
      }

      section.style.height = "100dvh";
      section.style.minHeight = "100dvh";

      // Force scroll to top
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });

      setTimeout(() => {
        init();
      }, 300);
    }
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      updateMask();
    }
  });

  // Initialize immediately on first load
  initImmediately();
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAboutSection);
} else {
  // DOM is already loaded
  initAboutSection();
}
